import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { 
  format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, 
  addDays, addWeeks, subWeeks, addMonths, subMonths, 
  isSameDay, isSameMonth, getDay, parseISO
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  ChevronLeft, ChevronRight, Plus, Clock, MapPin, Users, 
  CalendarIcon, List, CalendarDays, LayoutGrid, Edit2, Trash2, User, Archive, UserPlus, X, Repeat, UsersRound, Search, ChevronDown, UserX, Bell, Palmtree, Lock, Camera
} from 'lucide-react';
import axios from 'axios';
import { UploadMediaModal } from './Gallery';

// Planning module components and constants
import { 
  CourseDetailModal, 
  courseTypes,
  recurrenceTypes,
  durationHoursOptions,
  durationDaysOptions,
  getDefaultNewCours,
  WeekSeparator,
  CourseListCard,
  ListViewRenderer,
  CourseCalendarCard
} from '../components/planning';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Options de fourchettes de niveau galop
const GALOP_OPTIONS = [
  { value: '-', label: 'Non spécifié' },
  { value: '1-2', label: 'Galop 1-2' },
  { value: '2-3', label: 'Galop 2-3' },
  { value: '3-4', label: 'Galop 3-4' },
  { value: '4-5', label: 'Galop 4-5' },
  { value: '5-6', label: 'Galop 5-6' },
  { value: '6-7', label: 'Galop 6-7' },
  { value: '7-8', label: 'Galop 7-8' },
];

export const Planning = () => {
  const { user, isInstructor, isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Check if coming from absence action - show only my courses by default
  const actionParam = searchParams.get('action');
  const filterParam = searchParams.get('filter');
  const isAbsenceMode = actionParam === 'absence';
  const isAbsencesFilter = filterParam === 'absences';
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('list'); // 'list', 'week', 'month' - Default to 'list'
  const [selectedDayFilter, setSelectedDayFilter] = useState(null); // Filter list view to specific day when clicking from month/week view
  const [showOnlyMyCourses, setShowOnlyMyCourses] = useState(isAbsenceMode); // True if coming from absence action
  const [cours, setCours] = useState([]);
  const [themes, setThemes] = useState([]);
  const [instructeurs, setInstructeurs] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [vacances, setVacances] = useState([]); // Vacation periods
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCours, setSelectedCours] = useState(null);
  const [coursInscrits, setCoursInscrits] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingCours, setEditingCours] = useState(null);
  
  // Ref for scrolling to today's first course
  const todayRef = useRef(null);
  const hasScrolledToToday = useRef(false);
  
  // States for managing enrollments (instructors only)
  const [allCavaliers, setAllCavaliers] = useState([]);
  const [allInstructeurs, setAllInstructeurs] = useState([]);
  const [showAddCavalierModal, setShowAddCavalierModal] = useState(false);
  const [searchCavalier, setSearchCavalier] = useState('');
  
  // State for confirmation modal - supports different actions
  const [confirmModal, setConfirmModal] = useState({ 
    open: false, 
    type: null, // 'remove-cavalier', 'delete-cours', 'archive-cours', 'edit-cours-recurring'
    data: null,  // cavalier object or coursId or cours object (for edit)
    motif: '',   // reason for course deletion
    scope: 'one', // 'one' or 'future' for recurring courses
    isRecurring: false, // whether the course is recurring
    notify: true // Notify enrolled riders by default
  });
  
  // State for pending edit action (stored while showing recurring choice modal)
  const [pendingEditCours, setPendingEditCours] = useState(null);
  
  // State for newly created course (to scroll to it)
  const [newlyCreatedCoursId, setNewlyCreatedCoursId] = useState(null);
  
  // State for media upload modal
  const [mediaUploadOpen, setMediaUploadOpen] = useState(false);
  const [mediaUploadContext, setMediaUploadContext] = useState(null);
  
  // Search text state (unified search replaces multi-criteria filters)
  const [searchText, setSearchText] = useState('');
  
  // State for absences filter (from dashboard)
  const [showAbsencesOnly, setShowAbsencesOnly] = useState(isAbsencesFilter);
  const [absencesCoursIds, setAbsencesCoursIds] = useState([]);
  const [includeOtherInstructorsAbsences, setIncludeOtherInstructorsAbsences] = useState(false);
  
  // Notifications are now automatic - no checkbox needed
  
  const [newCours, setNewCours] = useState({
    theme_id: '',
    type_cours: 'collectif',
    instructeur_id: '',
    date_heure_debut: '',
    duree_minutes: 60,
    duree_jours: 1, // For stages
    recurrence: 'unique',
    groupe_fixe_id: '',
    places_max: 8,
    lieu: '',
    notes: '',
    niveau_galop: '',
    categorie_age: ''
  });
  
  // Age categories for courses
  const categoriesAge = [
    { value: 'adultes', label: 'Adultes' },
    { value: 'ados', label: 'Ados' },
    { value: 'enfants', label: 'Enfants' },
    { value: 'tout_petits', label: 'Tout petits' }
  ];
  
  // Helper function to check if a date is during vacation period
  const isDateInVacances = useCallback((date) => {
    if (!vacances || vacances.length === 0) return null;
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    for (const v of vacances) {
      const debut = new Date(v.date_debut);
      const fin = new Date(v.date_fin);
      debut.setHours(0, 0, 0, 0);
      fin.setHours(23, 59, 59, 999);
      
      if (checkDate >= debut && checkDate <= fin) {
        return v; // Return the vacation period info
      }
    }
    return null;
  }, [vacances]);
  
  // State for bulk course creation
  const [bulkCreate, setBulkCreate] = useState(false);
  const [dateFinSaison, setDateFinSaison] = useState('');

  // Calculate date range based on view mode
  const dateRange = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // When absences filter is active, always use extended range
    if (showAbsencesOnly) {
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth();
      const endYear = currentMonth >= 8 ? currentYear + 1 : currentYear;
      const endOfSeason = new Date(endYear, 5, 30); // June 30
      return {
        start: today, // From today
        end: endOfSeason
      };
    }
    
    if (viewMode === 'month') {
      return {
        start: startOfMonth(currentDate),
        end: endOfMonth(currentDate)
      };
    } else if (viewMode === 'list') {
      // For list view: load from start of current season to end of season
      // Season runs from September 1 to June 30
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth();
      
      // Determine start of current season
      // If we're before September (month < 8), season started September of previous year
      // If we're September or later, season started September of current year
      const seasonStartYear = currentMonth >= 8 ? currentYear : currentYear - 1;
      const startOfSeason = new Date(seasonStartYear, 8, 1); // September 1
      
      // Determine end of season
      // If we're before September, end of season is June 30 of current year
      // If we're September or later, end of season is June 30 of next year
      const endYear = currentMonth >= 8 ? currentYear + 1 : currentYear;
      const endOfSeason = new Date(endYear, 5, 30); // June 30
      
      // If filtering by a specific day, ensure that day's month is included in the range
      let rangeStart = startOfSeason;
      if (selectedDayFilter) {
        const filterMonthStart = startOfMonth(selectedDayFilter);
        if (filterMonthStart < rangeStart) {
          rangeStart = filterMonthStart;
        }
      }
      
      return {
        start: rangeStart,
        end: endOfSeason
      };
    } else {
      // For week view
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      // End of week should be end of Sunday (23:59:59) to include all Sunday courses
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
      return {
        start: weekStart,
        end: weekEnd
      };
    }
  }, [currentDate, viewMode, showAbsencesOnly, selectedDayFilter]);

  // Helper function to check if a course is in the past
  const isCoursePast = (coursDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const courseDay = new Date(coursDate);
    courseDay.setHours(0, 0, 0, 0);
    return courseDay < today;
  };

  // Check if user can modify a course (instructors can always modify, cavaliers cannot modify past courses)
  const canModifyCourse = (cours) => {
    if (isInstructor()) return true;
    return !isCoursePast(cours.date_heure_debut);
  };

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  // Ref for the scrollable content container
  const scrollContainerRef = useRef(null);
  
  // Ref for single click timer (to differentiate from double-click)
  const singleClickTimerRef = useRef(null);
  
  // Handle double-click to create course at specific date/time (Week view)
  const handleDoubleClickCreate = useCallback((day, hour) => {
    if (!isInstructor()) return;
    
    // Create date with selected day and hour
    const selectedDate = new Date(day);
    selectedDate.setHours(hour, 0, 0, 0);
    
    // Format for datetime-local input: YYYY-MM-DDTHH:MM
    const formattedDate = format(selectedDate, "yyyy-MM-dd'T'HH:mm");
    
    // Open create dialog with pre-filled date/time
    setNewCours(prev => ({
      ...prev,
      instructeur_id: user?.id || '',
      date_heure_debut: formattedDate
    }));
    setIsDialogOpen(true);
  }, [user, isInstructor]);
  
  // Handle single click on month cell (with delay to allow double-click)
  const handleMonthCellClick = useCallback((day) => {
    // Clear any pending single click
    if (singleClickTimerRef.current) {
      clearTimeout(singleClickTimerRef.current);
    }
    
    // Delay the single click action to allow double-click to cancel it
    singleClickTimerRef.current = setTimeout(() => {
      setSelectedDayFilter(day);
      setCurrentDate(day);
      setViewMode('list');
    }, 200);
  }, []);
  
  // Handle double-click to create course in Month view (hour based on click position)
  const handleMonthCellDoubleClick = useCallback((e, day) => {
    // Cancel the pending single click
    if (singleClickTimerRef.current) {
      clearTimeout(singleClickTimerRef.current);
      singleClickTimerRef.current = null;
    }
    
    if (!isInstructor()) return;
    
    // Get click position within the cell to determine hour
    const rect = e.currentTarget.getBoundingClientRect();
    const relativeY = e.clientY - rect.top;
    const cellHeight = rect.height;
    
    // Map Y position to hour (9h-18h = 9h range)
    // Top of cell = 9h, middle = 14h, bottom = 18h
    const startHour = 9;
    const endHour = 18;
    const hourRange = endHour - startHour; // 9 hours
    const clickPercent = Math.max(0, Math.min(1, relativeY / cellHeight));
    const hour = Math.floor(startHour + (clickPercent * hourRange));
    
    // Create date with selected day and calculated hour
    const selectedDate = new Date(day);
    selectedDate.setHours(hour, 0, 0, 0);
    
    // Format for datetime-local input: YYYY-MM-DDTHH:MM
    const formattedDate = format(selectedDate, "yyyy-MM-dd'T'HH:mm");
    
    // Open create dialog with pre-filled date/time
    setNewCours(prev => ({
      ...prev,
      instructeur_id: user?.id || '',
      date_heure_debut: formattedDate
    }));
    setIsDialogOpen(true);
  }, [user, isInstructor]);

  // Scroll to today's first course when list loads
  useEffect(() => {
    if (viewMode === 'list' && !loading && cours.length > 0 && !hasScrolledToToday.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        if (todayRef.current && scrollContainerRef.current) {
          // Scroll within the container, not the page
          const container = scrollContainerRef.current;
          const element = todayRef.current;
          const containerRect = container.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();
          
          // Calculate the scroll position to center the element in the container
          const scrollTop = element.offsetTop - container.offsetTop - (containerRect.height / 2) + (elementRect.height / 2);
          container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
          hasScrolledToToday.current = true;
        }
      }, 100);
    }
  }, [viewMode, loading, cours]);

  // Scroll to newly created course
  const newCoursRef = useRef(null);
  useEffect(() => {
    if (newlyCreatedCoursId && newCoursRef.current && scrollContainerRef.current) {
      // Scroll within the container, not the page
      const container = scrollContainerRef.current;
      const element = newCoursRef.current;
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      
      const scrollTop = element.offsetTop - container.offsetTop - (containerRect.height / 2) + (elementRect.height / 2);
      container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
      
      // Highlight effect
      newCoursRef.current.classList.add('ring-2', 'ring-green-500');
      setTimeout(() => {
        if (newCoursRef.current) {
          newCoursRef.current.classList.remove('ring-2', 'ring-green-500');
        }
        setNewlyCreatedCoursId(null);
      }, 2000);
    }
  }, [newlyCreatedCoursId, cours]);

  // Fetch absences data when filter is active
  useEffect(() => {
    if (isAbsencesFilter && isInstructor()) {
      fetchAbsencesData();
    }
  }, [isAbsencesFilter, includeOtherInstructorsAbsences]);

  const fetchAbsencesData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/cours-avec-absences`, {
        params: { include_others: includeOtherInstructorsAbsences }
      });
      const coursIds = (res.data.cours || []).map(c => c.id);
      setAbsencesCoursIds(coursIds);
    } catch (error) {
      console.error('Error fetching absences:', error);
      setAbsencesCoursIds([]);
    }
  };

  // Fetch all cavaliers for displaying group members and enrollment management
  useEffect(() => {
    // Always fetch cavaliers to display group member pseudos
    fetchAllCavaliers();
    if (isInstructor()) {
      fetchAllInstructeurs();
    }
  }, []);

  const fetchAllCavaliers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users`);
      // Filter to only show cavaliers (non-instructors)
      const cavaliers = res.data.filter(u => 
        u.statut === 'actif' && 
        !['instructeur', 'instructeur_admin'].includes(u.role_club)
      );
      setAllCavaliers(cavaliers);
    } catch (error) {
      console.error('Error fetching cavaliers:', error);
    }
  };

  const fetchAllInstructeurs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users`);
      // Filter to only show instructors
      const instructeurs = res.data.filter(u => 
        u.statut === 'actif' && 
        ['instructeur', 'instructeur_admin'].includes(u.role_club)
      );
      setAllInstructeurs(instructeurs);
    } catch (error) {
      console.error('Error fetching instructeurs:', error);
    }
  };

  const fetchData = async () => {
    try {
      // Fetch themes first (critical for course creation)
      const themesRes = await axios.get(`${API_URL}/api/themes`).catch(() => ({ data: [] }));
      setThemes(themesRes.data);
      
      // Fetch other data (non-critical for themes display)
      const [coursRes, usersRes, groupesRes, vacancesRes] = await Promise.all([
        axios.get(`${API_URL}/api/cours`, {
          params: {
            date_debut: dateRange.start.toISOString(),
            date_fin: dateRange.end.toISOString()
          }
        }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/users/pseudos`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/groupes`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/vacances`).catch(() => ({ data: [] })),
      ]);
      
      setCours(coursRes.data);
      setInstructeurs(usersRes.data);
      setGroupes(groupesRes.data);
      setVacances(vacancesRes.data);
    } catch (error) {
      console.error('Error fetching planning data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Admin functions to manage enrollments
  const handleAddCavalierToCours = async (cavalier) => {
    if (!selectedCours) return;
    
    try {
      await axios.post(`${API_URL}/api/cours/${selectedCours.id}/inscription-admin`, {
        cavalier_id: cavalier.id
      });
      toast.success(`${cavalier.pseudo || cavalier.prenom} inscrit(e) au cours`);
      setShowAddCavalierModal(false);
      setSearchCavalier('');
      
      // Refresh inscriptions
      const inscritsRes = await axios.get(`${API_URL}/api/cours/${selectedCours.id}/inscrits`);
      setCoursInscrits(inscritsRes.data.inscrits || []);
      
      // Refresh course data
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de l\'inscription'));
    }
  };

  const handleRemoveCavalierFromCours = async (cavalier) => {
    // Ouvrir la modal de confirmation
    setConfirmModal({ open: true, type: 'remove-cavalier', data: cavalier });
  };
  
  // Fonction appelée quand l'utilisateur confirme une action
  const handleConfirmAction = async () => {
    const { type, data, motif, scope, isRecurring, notify } = confirmModal;
    setConfirmModal({ open: false, type: null, data: null, motif: '', scope: 'one', isRecurring: false, notify: true });
    
    if (type === 'remove-cavalier') {
      // Supprimer cavalier du cours
      if (!selectedCours || !data?.cavalier_id) {
        toast.error('Erreur: données manquantes');
        return;
      }
      
      try {
        await axios.delete(`${API_URL}/api/cours/${selectedCours.id}/inscription-admin/${data.cavalier_id}`);
        toast.success('Cavalier retiré du cours (notification envoyée)');
        
        const inscritsRes = await axios.get(`${API_URL}/api/cours/${selectedCours.id}/inscrits`);
        setCoursInscrits(inscritsRes.data.inscrits || []);
        fetchData();
      } catch (error) {
        toast.error(getErrorMessage(error, 'Erreur lors de la désinscription'));
      }
    } else if (type === 'delete-cours') {
      // Supprimer le cours avec motif
      try {
        let response;
        const encodedMotif = motif ? encodeURIComponent(motif) : '';
        if (scope === 'future' && isRecurring) {
          // Delete this and all future occurrences
          response = await axios.delete(`${API_URL}/api/cours/${data}/recurring?notify=${notify}${encodedMotif ? `&motif=${encodedMotif}` : ''}`);
          const deletedCount = response.data.deleted_count || 0;
          const notifiedCount = response.data.notified_count || 0;
          toast.success(`${deletedCount} cours supprimés${notifiedCount > 0 ? ` (${notifiedCount} cavalier${notifiedCount > 1 ? 's' : ''} notifié${notifiedCount > 1 ? 's' : ''})` : ''}`);
        } else {
          // Delete single course
          response = await axios.delete(`${API_URL}/api/cours/${data}?notify=${notify}${encodedMotif ? `&motif=${encodedMotif}` : ''}`);
          const notifiedCount = response.data.notified_count || 0;
          toast.success(`Cours supprimé${notifiedCount > 0 ? ` (${notifiedCount} cavalier${notifiedCount > 1 ? 's' : ''} notifié${notifiedCount > 1 ? 's' : ''})` : ''}`);
        }
        setSelectedCours(null);
        fetchData();
      } catch (error) {
        toast.error('Erreur lors de la suppression du cours');
      }
    } else if (type === 'archive-cours') {
      // Archiver le cours
      try {
        await axios.patch(`${API_URL}/api/cours/${data}`, { statut: 'archive' });
        toast.success('Cours archivé');
        setSelectedCours(null);
        fetchData();
      } catch (error) {
        toast.error('Erreur lors de l\'archivage du cours');
      }
    } else if (type === 'edit-cours-recurring') {
      // This is handled by handleConfirmEditScope
      handleConfirmEditScope();
    }
  };

  // Filter cavaliers for search
  const filteredCavaliers = useMemo(() => {
    // First, filter by course criteria if available
    let eligibleCavaliers = allCavaliers;
    
    if (selectedCours) {
      eligibleCavaliers = allCavaliers.filter(c => {
        // Filter by age category if course has one
        if (selectedCours.categorie_age) {
          const birthDate = c.date_naissance ? new Date(c.date_naissance) : null;
          if (birthDate) {
            const today = new Date();
            const age = today.getFullYear() - birthDate.getFullYear();
            
            if (selectedCours.categorie_age === 'moins_18' && age >= 18) return false;
            if (selectedCours.categorie_age === 'plus_18' && age < 18) return false;
            if (selectedCours.categorie_age === 'moins_12' && age >= 12) return false;
            if (selectedCours.categorie_age === '12_18' && (age < 12 || age >= 18)) return false;
          }
        }
        
        // Filter by galop level if course has one
        if (selectedCours.niveau_galop) {
          const coursGalop = parseInt(selectedCours.niveau_galop);
          const cavalierGalop = c.niveau_galop ? parseInt(c.niveau_galop) : 0;
          // Allow cavaliers with galop level within range (coursGalop-1 to coursGalop+1)
          if (cavalierGalop < coursGalop - 1 || cavalierGalop > coursGalop + 1) return false;
        }
        
        return true;
      });
    }
    
    // Then apply search filter
    if (!searchCavalier.trim()) {
      // Show all eligible cavaliers (up to 50) when no search
      return eligibleCavaliers.slice(0, 50);
    }
    
    const search = searchCavalier.toLowerCase();
    return eligibleCavaliers.filter(c => 
      c.pseudo?.toLowerCase().includes(search) ||
      c.prenom?.toLowerCase().includes(search) ||
      c.nom?.toLowerCase().includes(search) ||
      c.email?.toLowerCase().includes(search)
    ).slice(0, 50);
  }, [allCavaliers, searchCavalier, selectedCours]);

  // Absence management functions
  const handleMarquerAbsent = async (cavalierId) => {
    if (!selectedCours) return;
    
    try {
      await axios.post(`${API_URL}/api/cours/${selectedCours.id}/absence-admin/${cavalierId}`);
      toast.success('Cavalier marqué absent');
      
      // Refresh inscriptions
      const inscritsRes = await axios.get(`${API_URL}/api/cours/${selectedCours.id}/inscrits`);
      setCoursInscrits(inscritsRes.data.inscrits || []);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur'));
    }
  };

  const handleAnnulerAbsence = async (cavalierId) => {
    if (!selectedCours) return;
    
    try {
      await axios.delete(`${API_URL}/api/cours/${selectedCours.id}/absence-admin/${cavalierId}`);
      toast.success('Absence annulée');
      
      // Refresh inscriptions
      const inscritsRes = await axios.get(`${API_URL}/api/cours/${selectedCours.id}/inscrits`);
      setCoursInscrits(inscritsRes.data.inscrits || []);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur'));
    }
  };

  // Cavalier declares absence
  const handleDeclarerAbsence = async () => {
    if (!selectedCours) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/cours/${selectedCours.id}/absence`);
      if (response.data.urgent) {
        toast.warning(response.data.message);
      } else {
        toast.success(response.data.message);
      }
      
      // Refresh inscriptions
      const inscritsRes = await axios.get(`${API_URL}/api/cours/${selectedCours.id}/inscrits`);
      setCoursInscrits(inscritsRes.data.inscrits || []);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la déclaration'));
    }
  };

  const handleAnnulerMaAbsence = async () => {
    if (!selectedCours) return;
    
    try {
      await axios.delete(`${API_URL}/api/cours/${selectedCours.id}/absence`);
      toast.success('Absence annulée');
      
      // Refresh inscriptions
      const inscritsRes = await axios.get(`${API_URL}/api/cours/${selectedCours.id}/inscrits`);
      setCoursInscrits(inscritsRes.data.inscrits || []);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur'));
    }
  };

  // Check if current user is enrolled and marked absent
  const isUserAbsent = useMemo(() => {
    if (!selectedCours || !user) return false;
    const inscrit = coursInscrits.find(i => i.cavalier_id === user.id);
    return inscrit?.absent || false;
  }, [selectedCours, coursInscrits, user]);

  // Check if current user is enrolled in the selected course
  const isUserEnrolled = useMemo(() => {
    if (!selectedCours || !user) return false;
    return coursInscrits.some(i => i.cavalier_id === user.id);
  }, [selectedCours, coursInscrits, user]);

  // Check if current user is enrolled via group (cannot unsubscribe, only mark absent)
  const isUserGroupMember = useMemo(() => {
    if (!selectedCours || !user) return false;
    const inscrit = coursInscrits.find(i => i.cavalier_id === user.id);
    return inscrit?.type === 'groupe_fixe';
  }, [selectedCours, coursInscrits, user]);

  const handleCreateCours = async () => {
    if (!newCours.theme_id || !newCours.date_heure_debut) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      // Clean up data - convert empty strings to null for optional fields
      const cleanedCours = {
        ...newCours,
        instructeur_id: newCours.instructeur_id || user.id,
        places_disponibles: newCours.places_max,
        groupe_fixe_id: newCours.groupe_fixe_id || null,
        categorie_age: newCours.categorie_age || null,
        niveau_galop: newCours.niveau_galop || null,
        lieu: newCours.lieu || null,
        notes: newCours.notes || null,
        notify_cavaliers: newCours.type_cours !== 'particulier' // Auto-notify for collective courses
      };
      
      // Remove duree_jours from the payload (only used for UI)
      delete cleanedCours.duree_jours;
      
      console.log('Creating course with payload:', cleanedCours);
      
      // Check if bulk creation is requested
      if (bulkCreate && newCours.recurrence === 'hebdomadaire' && dateFinSaison) {
        const response = await axios.post(`${API_URL}/api/cours/bulk-create`, {
          cours: cleanedCours,
          date_fin_saison: dateFinSaison,
          notify_cavaliers: newCours.type_cours !== 'particulier'
        });
        
        const { created_count, skipped_count, first_cours_id } = response.data;
        let message = `${created_count} cours créés`;
        if (skipped_count > 0) {
          message += ` (${skipped_count} dates en vacances ignorées)`;
        }
        if (newCours.type_cours !== 'particulier') {
          message += ' - Cavaliers notifiés';
        }
        toast.success(message);
        
        // Set the first created course to scroll to
        if (first_cours_id) {
          setNewlyCreatedCoursId(first_cours_id);
        }
      } else {
        // Single course creation
        const response = await axios.post(`${API_URL}/api/cours`, cleanedCours);
        let message = 'Cours créé avec succès';
        if (newCours.type_cours !== 'particulier') {
          message += ' - Cavaliers notifiés';
        }
        toast.success(message);
        
        // Set newly created course ID to scroll to it
        if (response.data?.id) {
          setNewlyCreatedCoursId(response.data.id);
        }
      }
      
      setIsDialogOpen(false);
      setBulkCreate(false);
      setDateFinSaison('');
      setNewCours({
        theme_id: '',
        type_cours: 'collectif',
        instructeur_id: '',
        date_heure_debut: '',
        duree_minutes: 60,
        duree_jours: 1,
        recurrence: 'unique',
        groupe_fixe_id: '',
        places_max: 8,
        lieu: '',
        notes: '',
        niveau_galop: '',
        categorie_age: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error creating course:', error);
      // Handle validation errors from Pydantic (detail can be array or string)
      const detail = error.response?.data?.detail;
      let errorMessage = 'Erreur lors de la création du cours';
      if (detail) {
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
      }
      toast.error(errorMessage);
    }
  };

  const handleEditCours = (cours) => {
    // Convert the date to local datetime-local format
    const dateObj = new Date(cours.date_heure_debut);
    const localDate = new Date(dateObj.getTime() - dateObj.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    
    const coursWithLocalDate = {
      ...cours,
      date_heure_debut: localDate
    };
    
    // If the course is recurring, show scope selection modal first
    if (cours.recurrence === 'hebdomadaire') {
      setPendingEditCours(coursWithLocalDate);
      setConfirmModal({
        open: true,
        type: 'edit-cours-recurring',
        data: cours,
        motif: '',
        scope: 'one',
        isRecurring: true
      });
    } else {
      // Non-recurring course: open edit modal directly
      setEditingCours(coursWithLocalDate);
      setSelectedCours(null);
      setIsEditMode(true);
    }
  };

  // Called when user confirms scope for editing recurring course
  const handleConfirmEditScope = () => {
    if (pendingEditCours) {
      setEditingCours({
        ...pendingEditCours,
        _editScope: confirmModal.scope // Store scope for later use when saving
      });
      setSelectedCours(null);
      setIsEditMode(true);
    }
    setPendingEditCours(null);
    setConfirmModal({ open: false, type: null, data: null, motif: '', scope: 'one', isRecurring: false, notify: true });
  };

  const handleUpdateCours = async () => {
    // Debug: log the editingCours state
    console.log('handleUpdateCours called with editingCours:', JSON.stringify(editingCours, null, 2));
    
    if (!editingCours || !editingCours.theme_id) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    // Validate places_max is a valid number
    const placesMax = parseInt(editingCours.places_max);
    if (isNaN(placesMax) || placesMax < 1) {
      toast.error('Le nombre de places doit être un nombre valide supérieur à 0');
      return;
    }
    
    try {
      const editScope = editingCours._editScope;
      let response;
      
      if (editScope && editingCours.recurrence === 'hebdomadaire') {
        // Use the recurring update endpoint with ONLY modifiable fields
        // BUSINESS RULE: groupe_fixe_id, categorie_age, niveau_galop, recurrence, type_cours are LOCKED
        const payload = {
          scope: editScope,
          places_max: placesMax,
          lieu: editingCours.lieu || '',
          notes: editingCours.notes || '',
          theme_id: editingCours.theme_id,
          instructeur_id: editingCours.instructeur_id,
          notify_cavaliers: true // Always notify enrolled riders on modification
        };
        
        // For 'single' scope: include date/time only for this specific course
        // For 'future' scope: include time for all future courses
        if (editScope === 'single' || editScope === 'one') {
          payload.date_heure_debut = editingCours.date_heure_debut;
          payload.duree_minutes = parseInt(editingCours.duree_minutes) || 60;
        } else if (editScope === 'future') {
          payload.date_heure_debut = editingCours.date_heure_debut;
          payload.duree_minutes = parseInt(editingCours.duree_minutes) || 60;
        }
        
        console.log('Sending PUT request with payload:', payload);
        response = await axios.put(`${API_URL}/api/cours/${editingCours.id}/recurring`, payload);
        const count = response.data.updated_count || 1;
        toast.success(`${count} cours modifié${count > 1 ? 's' : ''} - Cavaliers inscrits notifiés`);
      } else {
        // Standard single course update (use PATCH for partial update)
        // BUSINESS RULE: Only send modifiable fields for collective courses
        const payload = {
          theme_id: editingCours.theme_id,
          instructeur_id: editingCours.instructeur_id,
          date_heure_debut: editingCours.date_heure_debut,
          duree_minutes: parseInt(editingCours.duree_minutes) || 60,
          places_max: placesMax,
          lieu: editingCours.lieu || '',
          notes: editingCours.notes || '',
          notify_cavaliers: true // Always notify enrolled riders on modification
        };
        console.log('Sending PATCH request with payload:', payload);
        response = await axios.patch(`${API_URL}/api/cours/${editingCours.id}`, payload);
        toast.success('Cours modifié - Cavaliers inscrits notifiés');
      }
      
      setIsEditMode(false);
      setEditingCours(null);
      // Refresh data to show updated course
      await fetchData();
    } catch (error) {
      console.error('Error updating course:', error);
      console.error('Error response:', error.response?.data);
      const detail = error.response?.data?.detail;
      let errorMessage = 'Erreur lors de la modification du cours';
      if (detail) {
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
      }
      toast.error(errorMessage);
    }
  };

  const handleDeleteCours = async (coursId, isRecurring = false) => {
    // Ouvrir la modal de confirmation avec info de récurrence
    setConfirmModal({ 
      open: true, 
      type: 'delete-cours', 
      data: coursId, 
      motif: '', 
      scope: 'one',
      isRecurring,
      notify: true  // Always notify - handled by user preferences system
    });
  };

  // Archive a course (only for admin instructors)
  const handleArchiveCours = async (coursId) => {
    // Ouvrir la modal de confirmation
    setConfirmModal({ open: true, type: 'archive-cours', data: coursId, motif: '', scope: 'one', isRecurring: false, notify: true });
  };

  const handleSelectCours = async (cours) => {
    setSelectedCours(cours);
    // Auto-load the enrolled riders
    try {
      const res = await axios.get(`${API_URL}/api/cours/${cours.id}/inscrits`);
      setCoursInscrits(res.data.inscrits || []);
    } catch (e) {
      console.error('Error fetching inscrits:', e);
      setCoursInscrits([]);
    }
  };

  const handleInscription = async (coursId) => {
    try {
      await axios.post(`${API_URL}/api/cours/${coursId}/inscription`);
      toast.success('Inscription enregistrée');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de l\'inscription'));
    }
  };

  const handleDesinscription = async (coursId) => {
    try {
      await axios.delete(`${API_URL}/api/cours/${coursId}/inscription`);
      toast.success('Désinscription effectuée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la désinscription');
    }
  };

  const getThemeColor = (themeId) => {
    const theme = themes.find(t => t.id === themeId);
    return theme?.couleur || '#57534E';
  };

  const getThemeName = (themeId) => {
    const theme = themes.find(t => t.id === themeId);
    return theme?.nom_abrege || theme?.nom || 'Cours';
  };

  const getThemeFullName = (themeId) => {
    const theme = themes.find(t => t.id === themeId);
    return theme?.nom_complet || theme?.nom || 'Cours';
  };

  const getInstructeurName = (instrId) => {
    const instr = instructeurs.find(i => i.id === instrId);
    // Return pseudo if available, otherwise full name
    return instr ? (instr.pseudo || `${instr.prenom} ${instr.nom}`) : '';
  };

  const getGroupeName = (groupeId) => {
    const groupe = groupes.find(g => g.id === groupeId);
    return groupe?.nom || '';
  };

  // Get list of enrolled riders' pseudos for a course (with absence info and source type)
  const getInscritsForCours = (cours) => {
    const inscrits = [];
    const absences = cours.absences || [];
    const absenceIds = absences.map(a => a.cavalier_id);
    
    // Helper to find user by ID (check both instructeurs and allCavaliers)
    const findUserById = (userId) => {
      return instructeurs.find(u => u.id === userId) || 
             allCavaliers.find(u => u.id === userId);
    };
    
    // Get group member IDs for checking if ponctual inscription is also in group
    const groupMemberIds = new Set();
    
    // Add group members if this is a group course
    if (cours.groupe_fixe_id) {
      const groupe = groupes.find(g => g.id === cours.groupe_fixe_id);
      if (groupe?.cavaliers_membres && groupe.cavaliers_membres.length > 0) {
        groupe.cavaliers_membres.forEach(cavId => {
          groupMemberIds.add(cavId);
          const cavalier = findUserById(cavId);
          if (cavalier) {
            const pseudo = cavalier.pseudo || `${cavalier.prenom} ${cavalier.nom?.[0] || ''}.`;
            const isAbsent = absenceIds.includes(cavId);
            inscrits.push({ 
              pseudo, 
              absent: isAbsent, 
              type: 'groupe_fixe',
              cavalier_id: cavId
            });
          }
        });
      }
    }
    
    // Add ponctual inscriptions (riders not in group)
    if (cours.inscriptions_ponctuelles) {
      cours.inscriptions_ponctuelles.forEach(inscription => {
        // Handle both string (legacy) and object (new) formats
        let cavalier_id;
        let isAbsent = false;
        
        if (typeof inscription === 'string') {
          // Legacy format: just the user ID
          cavalier_id = inscription;
        } else if (typeof inscription === 'object' && inscription !== null) {
          // New format: object with cavalier_id
          cavalier_id = inscription.cavalier_id;
          isAbsent = inscription.absent || false;
        } else {
          return; // Skip invalid entries
        }
        
        const cavalier = findUserById(cavalier_id);
        if (cavalier) {
          const basePseudo = cavalier.pseudo || `${cavalier.prenom} ${cavalier.nom?.[0] || ''}.`;
          // Check if already in list (from group) - skip if already in group
          if (!groupMemberIds.has(cavalier_id)) {
            inscrits.push({ 
              pseudo: `+${basePseudo}`, // Add "+" prefix for ponctual inscriptions
              absent: isAbsent, 
              type: 'ponctuel',
              cavalier_id: cavalier_id
            });
          }
        }
      });
    }
    
    return inscrits;
  };

  // Filter courses based on user preference and exclude archived courses
  const filteredCours = useMemo(() => {
    // First, exclude archived courses
    let filtered = cours.filter(c => c.statut !== 'archive');
    
    // Apply absences filter first (if coming from dashboard)
    if (showAbsencesOnly && absencesCoursIds.length > 0) {
      filtered = filtered.filter(c => absencesCoursIds.includes(c.id));
    }
    
    if (showOnlyMyCourses) {
      filtered = filtered.filter(c => {
        // Check if user is the instructor
        if (c.instructeur_id === user?.id) return true;
        // Check if user is enrolled
        const enrolled = c.inscriptions_ponctuelles?.some(i => i.cavalier_id === user?.id);
        if (enrolled) return true;
        // Check if user is in the group
        if (c.groupe_fixe_id) {
          const groupe = groupes.find(g => g.id === c.groupe_fixe_id);
          if (groupe?.cavaliers_membres?.includes(user?.id)) return true;
        }
        return false;
      });
    }
    
    // Unified search across all course fields
    if (searchText.trim()) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(c => {
        const theme = themes.find(t => t.id === c.theme_id);
        const instructeur = instructeurs.find(i => i.id === c.instructeur_id);
        const groupe = groupes.find(g => g.id === c.groupe_fixe_id);
        
        // Get enrolled riders names
        const inscritsNames = [];
        // From group
        if (groupe?.cavaliers_membres) {
          groupe.cavaliers_membres.forEach(cavId => {
            const cav = instructeurs.find(u => u.id === cavId) || allCavaliers.find(u => u.id === cavId);
            if (cav) {
              inscritsNames.push(cav.prenom?.toLowerCase(), cav.nom?.toLowerCase(), cav.pseudo?.toLowerCase());
            }
          });
        }
        // From ponctual inscriptions
        if (c.inscriptions_ponctuelles) {
          c.inscriptions_ponctuelles.forEach(insc => {
            const cavId = typeof insc === 'string' ? insc : insc?.cavalier_id;
            const cav = instructeurs.find(u => u.id === cavId) || allCavaliers.find(u => u.id === cavId);
            if (cav) {
              inscritsNames.push(cav.prenom?.toLowerCase(), cav.nom?.toLowerCase(), cav.pseudo?.toLowerCase());
            }
          });
        }
        
        // Format date for search (e.g., "lundi 10 février", "10/02", "février")
        const courseDate = new Date(c.date_heure_debut);
        const dateStr = format(courseDate, "EEEE d MMMM yyyy", { locale: fr }).toLowerCase();
        const dateShort = format(courseDate, "dd/MM", { locale: fr });
        const timeStr = format(courseDate, "HH:mm", { locale: fr });
        
        // Map category age values to searchable labels
        const ageLabels = {
          'adultes': 'adultes adulte',
          'ados': 'ados adolescents',
          'enfants': 'enfants enfant',
          'tout_petits': 'tout petits baby poney'
        };
        const ageCategory = c.categorie_age || c.age_category || '';
        const ageSearchable = ageLabels[ageCategory] || ageCategory;
        
        // Map galop levels to searchable labels
        const galopLabel = c.niveau_galop ? `galop ${c.niveau_galop} g${c.niveau_galop}` : '';
        
        // Map course types to searchable labels
        const typeLabels = {
          'collectif': 'collectif cours collectif',
          'particulier': 'particulier cours particulier individuel',
          'stage': 'stage',
          'balade': 'balade promenade',
          'concours': 'concours compétition'
        };
        const courseType = c.type_cours || c.type_activite || '';
        const typeSearchable = typeLabels[courseType] || courseType;
        
        return (
          // Theme
          theme?.nom?.toLowerCase().includes(search) ||
          theme?.nom_complet?.toLowerCase().includes(search) ||
          theme?.nom_abrege?.toLowerCase().includes(search) ||
          // Instructor
          instructeur?.prenom?.toLowerCase().includes(search) ||
          instructeur?.nom?.toLowerCase().includes(search) ||
          instructeur?.pseudo?.toLowerCase().includes(search) ||
          // Group
          groupe?.nom?.toLowerCase().includes(search) ||
          // Location
          c.lieu?.toLowerCase().includes(search) ||
          // Notes
          c.notes?.toLowerCase().includes(search) ||
          // Course type
          typeSearchable.toLowerCase().includes(search) ||
          // Age category
          ageSearchable.toLowerCase().includes(search) ||
          // Galop level
          galopLabel.toLowerCase().includes(search) ||
          // Date & time
          dateStr.includes(search) ||
          dateShort.includes(search) ||
          timeStr.includes(search) ||
          // Enrolled riders
          inscritsNames.some(name => name?.includes(search))
        );
      });
    }
    
    return filtered;
  }, [cours, showOnlyMyCourses, user, groupes, searchText, themes, instructeurs, allCavaliers, showAbsencesOnly, absencesCoursIds]);

  // Reset all filters
  // Calculate real available places (accounting for groupe fixe members + ponctual inscriptions)
  const getPlacesDisponibles = (cours) => {
    const placesMax = cours.places_max || 0;
    let totalInscrits = 0;
    
    // Count groupe fixe members (excluding absents)
    if (cours.groupe_fixe_id) {
      const groupe = groupes.find(g => g.id === cours.groupe_fixe_id);
      if (groupe) {
        const membres = groupe.cavaliers_membres || [];
        const absents = (cours.absences || []).map(a => typeof a === 'string' ? a : a.cavalier_id);
        totalInscrits += membres.filter(m => !absents.includes(m)).length;
      }
    }
    
    // Count ponctual inscriptions (excluding absents)
    const inscriptions = cours.inscriptions_ponctuelles || [];
    for (const insc of inscriptions) {
      const cavId = typeof insc === 'string' ? insc : insc.cavalier_id;
      const isAbsent = typeof insc === 'object' && insc.absent;
      if (!isAbsent) {
        totalInscrits++;
      }
    }
    
    return Math.max(0, placesMax - totalInscrits);
  };

  // For list view: filter and sort courses starting from today
  const listViewCours = useMemo(() => {
    let coursToShow = [...filteredCours];
    
    // If a specific day is selected (from month/week view click), filter to that day only
    if (selectedDayFilter) {
      coursToShow = coursToShow.filter(c => {
        const courseDate = new Date(c.date_heure_debut);
        return isSameDay(courseDate, selectedDayFilter);
      });
    }
    
    // Sort by start time
    return coursToShow.sort((a, b) => new Date(a.date_heure_debut) - new Date(b.date_heure_debut));
  }, [filteredCours, selectedDayFilter]);

  // Pre-compute grouped course structure for list view (vacation blocks, week separators)
  const groupedListViewData = useMemo(() => {
    if (listViewCours.length === 0) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Find scroll target index
    const scrollTargetIndex = listViewCours.findIndex(c => {
      const courseDate = new Date(c.date_heure_debut);
      courseDate.setHours(0, 0, 0, 0);
      return courseDate >= today;
    });
    const actualScrollIndex = scrollTargetIndex === -1 ? 0 : scrollTargetIndex;

    // Build structured groups
    const groups = [];
    let currentVacationGroup = null;
    let lastWeekKey = null;

    for (let i = 0; i < listViewCours.length; i++) {
      const c = listViewCours[i];
      const coursDate = new Date(c.date_heure_debut);
      const weekStart = startOfWeek(coursDate, { weekStartsOn: 1 });
      const weekEnd = addDays(weekStart, 6);
      const weekKey = format(weekStart, 'yyyy-MM-dd');
      const vacancePeriod = isDateInVacances(coursDate);
      const weekVacancePeriod = isDateInVacances(weekStart);
      const isNewWeek = weekKey !== lastWeekKey;
      
      // Course metadata
      const courseData = {
        type: 'course',
        course: c,
        isScrollTarget: i === actualScrollIndex,
        isNewlyCreated: c.id === newlyCreatedCoursId,
        isPast: isCoursePast(coursDate),
        coursDate
      };

      // Week separator data
      const weekSeparatorData = {
        type: 'week-separator',
        key: `week-sep-${weekKey}`,
        weekStart,
        weekEnd,
        vacancePeriod: weekVacancePeriod,
        isInVacation: !!vacancePeriod
      };

      if (vacancePeriod) {
        // Handle vacation courses
        if (!currentVacationGroup) {
          // Start a new vacation group
          currentVacationGroup = {
            type: 'vacation-group',
            key: `vacation-${c.id}`,
            items: []
          };
          
          // If this is a new week, add the separator INSIDE the vacation group
          if (isNewWeek) {
            currentVacationGroup.items.push(weekSeparatorData);
          }
        } else {
          // Already in vacation group, add week separator if new week
          if (isNewWeek) {
            currentVacationGroup.items.push(weekSeparatorData);
          }
        }
        
        currentVacationGroup.items.push(courseData);
      } else {
        // Non-vacation course
        // First, flush any pending vacation group
        if (currentVacationGroup) {
          groups.push(currentVacationGroup);
          currentVacationGroup = null;
        }
        
        // Add week separator if new week
        if (isNewWeek) {
          groups.push({
            ...weekSeparatorData,
            isInVacation: false
          });
        }
        
        groups.push(courseData);
      }
      
      lastWeekKey = weekKey;
    }
    
    // Flush any remaining vacation group
    if (currentVacationGroup) {
      groups.push(currentVacationGroup);
    }
    
    return groups;
  }, [listViewCours, newlyCreatedCoursId, isDateInVacances]);

  const weekDays = Array.from({ length: 7 }, (_, i) => 
    addDays(startOfWeek(currentDate, { weekStartsOn: 1 }), i)
  );

  const getCoursForDay = (date) => {
    return filteredCours.filter(c => {
      const coursDate = new Date(c.date_heure_debut);
      return isSameDay(coursDate, date);
    }).sort((a, b) => new Date(a.date_heure_debut) - new Date(b.date_heure_debut));
  };

  // Generate month calendar days
  const monthDays = useMemo(() => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    const startWeek = startOfWeek(start, { weekStartsOn: 1 });
    const endWeek = endOfWeek(end, { weekStartsOn: 1 });
    
    const days = [];
    let day = startWeek;
    while (day <= endWeek) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  }, [currentDate]);

  const getDateLabel = () => {
    if (viewMode === 'month') {
      return format(currentDate, 'MMMM yyyy', { locale: fr });
    }
    return `${format(startOfWeek(currentDate, { weekStartsOn: 1 }), "d MMM", { locale: fr })} - ${format(endOfWeek(currentDate, { weekStartsOn: 1 }), "d MMM yyyy", { locale: fr })}`;
  };

  // Helper to generate course title: "Cours adultes dressage" or just theme name
  const getCoursTitle = (cours) => {
    const themeName = getThemeFullName(cours.theme_id);
    if (cours.categorie_age) {
      const ageLabel = categoriesAge.find(c => c.value === cours.categorie_age)?.label || '';
      return `${ageLabel} ${themeName.toLowerCase()}`;
    }
    return themeName;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn flex flex-col h-[calc(100vh-130px)] md:h-[calc(100vh-100px)]" data-testid="planning-page">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white pb-3 border-b border-stone-200">
        {/* Row 1: Title + Date Label + New Button */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <h1 className="text-lg md:text-xl font-serif font-bold text-[#2E4A3D]">
              {isAbsenceMode ? 'Absence' : 'Planning'}
            </h1>
            {/* Date label - Always visible, larger on week/month views */}
            {viewMode === 'list' ? (
              <span className="text-xs text-stone-500 hidden md:inline">{getDateLabel()}</span>
            ) : (
              <span className="text-sm md:text-base font-semibold text-stone-700 capitalize">{getDateLabel()}</span>
            )}
          </div>
          
          {/* New Course Button */}
          {isInstructor() && (
            <Button 
              className="btn-action h-8 text-xs px-3"
              onClick={() => {
                setNewCours(prev => ({...prev, instructeur_id: user?.id || ''}));
                setIsDialogOpen(true);
              }}
              data-testid="new-cours-btn-desktop"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              <span className="hidden sm:inline">Nouveau</span>
            </Button>
          )}
        </div>
        
        {/* Row 2: View Mode Tabs - Full Width */}
        <div className="mt-2">
          <Tabs value={viewMode} onValueChange={(mode) => {
            setSelectedDayFilter(null);
            setViewMode(mode);
          }} className="w-full">
            <TabsList className="w-full h-9 grid grid-cols-3">
              <TabsTrigger value="list" className="text-sm h-8"><List className="w-4 h-4 mr-1.5" />Liste</TabsTrigger>
              <TabsTrigger value="week" className="text-sm h-8"><CalendarDays className="w-4 h-4 mr-1.5" />Semaine</TabsTrigger>
              <TabsTrigger value="month" className="text-sm h-8"><LayoutGrid className="w-4 h-4 mr-1.5" />Mois</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        
        {/* Row 3: Search + My Courses + Navigation + Counter */}
        <div className="flex flex-wrap items-center gap-2 mt-2">
          {/* Search field */}
          <div className="relative flex-1 min-w-[100px] max-w-[250px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input 
              placeholder="Rechercher..." 
              value={searchText} 
              onChange={(e) => setSearchText(e.target.value)} 
              className="pl-8 h-8 text-sm"
            />
            {searchText && (
              <button 
                onClick={() => setSearchText('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          
          <label className="flex items-center gap-1.5 bg-[#2E4A3D]/10 px-2 py-1.5 rounded border border-[#2E4A3D]/20 cursor-pointer h-8">
            <Checkbox id="my-courses" checked={showOnlyMyCourses} onCheckedChange={setShowOnlyMyCourses} className="h-3.5 w-3.5 border-[#2E4A3D] data-[state=checked]:bg-[#2E4A3D]" />
            <span className="text-xs font-medium text-[#2E4A3D]">Mes cours</span>
          </label>
          
          {/* Navigation controls for week/month views - Right after Mes cours */}
          {(viewMode === 'week' || viewMode === 'month') && (
            <div className="flex items-center gap-0.5">
              <button 
                onClick={() => setCurrentDate(viewMode === 'week' ? subWeeks(currentDate, 1) : subMonths(currentDate, 1))}
                className="p-1 rounded hover:bg-stone-100 transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-stone-500" />
              </button>
              <button 
                onClick={() => setCurrentDate(new Date())}
                className="px-1.5 text-xs text-stone-500 hover:text-stone-700 hover:underline transition-colors"
              >
                Auj.
              </button>
              <button 
                onClick={() => setCurrentDate(viewMode === 'week' ? addWeeks(currentDate, 1) : addMonths(currentDate, 1))}
                className="p-1 rounded hover:bg-stone-100 transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-stone-500" />
              </button>
            </div>
          )}
          
          {/* Spacer */}
          <div className="flex-1"></div>
          
          <span className="text-xs text-stone-400">{selectedDayFilter ? listViewCours.length : filteredCours.length} cours</span>
        </div>
        
        {/* Day filter banner */}
        {selectedDayFilter && viewMode === 'list' && (
          <div className="mt-2 bg-blue-50 border border-blue-200 rounded px-2 py-1.5 text-blue-800 text-xs flex items-center justify-between gap-2">
            <span className="flex items-center gap-1">
              <CalendarIcon className="w-3.5 h-3.5" />
              <span className="font-medium">{format(selectedDayFilter, "EEEE d MMMM yyyy", { locale: fr })}</span>
            </span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setSelectedDayFilter(null)} 
              className="h-6 px-2 text-blue-700 hover:text-blue-900 hover:bg-blue-100"
            >
              <X className="w-3 h-3 mr-1" />
              Voir tout
            </Button>
          </div>
        )}
        
        {/* Banners */}
        {isAbsenceMode && (
          <div className="mt-2 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 text-amber-700 text-xs">
            <span className="font-medium">Mode absence</span> — Seuls vos cours sont affichés
          </div>
        )}
        {showAbsencesOnly && isInstructor() && (
          <div className="mt-2 bg-orange-50 border border-orange-200 rounded px-2 py-1.5 text-orange-800 text-xs flex items-center justify-between gap-2">
            <span className="flex items-center gap-1"><UserX className="w-3.5 h-3.5" /><span className="font-medium">Absences ({absencesCoursIds.length})</span></span>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1 cursor-pointer"><Checkbox checked={includeOtherInstructorsAbsences} onCheckedChange={setIncludeOtherInstructorsAbsences} className="h-3 w-3" /><span>+ autres</span></label>
              <Button variant="ghost" size="sm" onClick={() => setShowAbsencesOnly(false)} className="h-6 px-1"><X className="w-3 h-3" /></Button>
            </div>
          </div>
        )}
      </div>

      {/* Scrollable Content */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto overflow-x-hidden pt-4" style={{ minHeight: 0, touchAction: 'pan-y' }}>

      {/* LIST VIEW */}
      {viewMode === 'list' && (
        <div className="overflow-x-hidden">
          {listViewCours.length === 0 ? (
            <div className="empty-state">
              <CalendarDays className="empty-state-icon" />
              <h3 className="empty-state-title">Aucun cours</h3>
              <p className="empty-state-description">Aucun cours prévu pour cette période</p>
            </div>
          ) : (
            <ListViewRenderer 
              groupedData={groupedListViewData}
              todayRef={todayRef}
              newCoursRef={newCoursRef}
              getThemeColor={getThemeColor}
              getCoursTitle={getCoursTitle}
              getInstructeurName={getInstructeurName}
              getGroupeName={getGroupeName}
              getInscritsForCours={getInscritsForCours}
              handleSelectCours={handleSelectCours}
            />
          )}
        </div>
      )}

      {/* WEEK VIEW - Time Grid Style */}
      {viewMode === 'week' && (
        <div className="bg-white rounded-lg border">
          {/* Header with days - sticky */}
          <div className="grid grid-cols-[35px_repeat(7,1fr)] md:grid-cols-[50px_repeat(7,1fr)] border-b bg-stone-50 sticky top-0 z-10">
            <div className="p-1.5 border-r border-stone-200 bg-stone-50"></div>
            {weekDays.map((day, index) => {
              const isToday = isSameDay(day, new Date());
              const vacancePeriod = isDateInVacances(day);
              return (
                <div 
                  key={index} 
                  className={`p-1.5 md:p-2 text-center border-r border-stone-200 last:border-r-0 cursor-pointer hover:opacity-80 ${
                    isToday ? 'bg-[#2E4A3D] text-white' : vacancePeriod ? 'bg-red-100' : 'bg-stone-50'
                  }`}
                  onDoubleClick={() => {
                    setSelectedDayFilter(day);
                    setCurrentDate(day);
                      setViewMode('list');
                    }}
                    title="Double-clic pour voir les cours de ce jour"
                  >
                    <p className={`text-xs md:text-sm uppercase ${isToday ? 'text-white/70' : vacancePeriod ? 'text-red-600' : 'text-stone-500'}`}>
                      {format(day, 'EEE', { locale: fr })}
                    </p>
                    <p className={`text-base md:text-xl font-bold ${isToday ? 'text-white' : vacancePeriod ? 'text-red-700' : 'text-stone-800'}`}>
                      {format(day, 'd', { locale: fr })}
                    </p>
                  </div>
                );
              })}
            </div>
          
          {/* Time grid - hours from 7h to 21h */}
          <div className="relative">
            {Array.from({ length: 15 }, (_, i) => i + 7).map((hour) => (
              <div key={hour} className="grid grid-cols-[35px_repeat(7,1fr)] md:grid-cols-[50px_repeat(7,1fr)] border-b border-stone-200 last:border-b-0" style={{ minHeight: '50px' }}>
                {/* Hour label */}
                <div className="p-0.5 border-r border-stone-300 text-xs md:text-sm text-stone-400 text-right pr-1 bg-stone-50/50">
                  {hour}h
                </div>
                
                {/* Day columns */}
                {weekDays.map((day, dayIndex) => {
                  const isToday = isSameDay(day, new Date());
                  const vacancePeriod = isDateInVacances(day);
                  // Get courses that start at this hour
                  const hourCours = getCoursForDay(day).filter(c => {
                    const courseHour = new Date(c.date_heure_debut).getHours();
                    return courseHour === hour;
                  });
                  
                  return (
                    <div 
                      key={dayIndex} 
                      className={`border-r border-stone-200 last:border-r-0 p-0.5 relative ${
                        isToday ? 'bg-green-50/30' : vacancePeriod ? 'bg-red-50/40' : ''
                      } ${isInstructor() ? 'cursor-pointer hover:bg-stone-50' : ''}`}
                      onDoubleClick={() => handleDoubleClickCreate(day, hour)}
                      title={isInstructor() ? "Double-clic pour créer un cours" : ""}
                    >
                      {hourCours.map(c => {
                        const theme = themes.find(t => t.id === c.theme_id);
                        const isPast = isCoursePast(c.date_heure_debut);
                        const startMinutes = new Date(c.date_heure_debut).getMinutes();
                        const duration = c.duree_minutes || 60;
                        const heightPercent = Math.min((duration / 60) * 100, 200);
                        const themeColor = theme?.couleur || '#6B7280';
                        const instructeur = instructeurs.find(u => u.id === c.instructeur_id);
                        
                        // Get short theme label
                        const themeLabel = theme?.nom_abrege || theme?.nom?.substring(0, 8) || '';
                        
                        // Get age category label
                        const ageLabels = {
                          'adultes': 'Ad.',
                          'ados': 'Ado',
                          'enfants': 'Enf.',
                          'tout_petits': 'TP'
                        };
                        const ageLabel = c.categorie_age ? ageLabels[c.categorie_age] || '' : '';
                        
                        return (
                          <div
                            key={c.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectCours(c);
                            }}
                            onDoubleClick={(e) => e.stopPropagation()}
                            className={`absolute left-0.5 right-0.5 rounded px-1.5 py-1 cursor-pointer overflow-hidden transition-all hover:z-10 hover:shadow-md border ${
                              isPast ? 'opacity-70' : ''
                            }`}
                            style={{
                              backgroundColor: themeColor + '25',
                              borderColor: themeColor,
                              top: `${(startMinutes / 60) * 100}%`,
                              height: `${heightPercent}%`,
                              minHeight: '40px',
                              zIndex: 1
                            }}
                            data-testid={`week-course-${c.id}`}
                          >
                            <p className="text-[10px] md:text-xs font-semibold truncate" style={{ color: themeColor }}>
                              {format(new Date(c.date_heure_debut), 'HH:mm')} {themeLabel}
                            </p>
                            <p className="text-[9px] md:text-[11px] text-stone-600 truncate">
                              {ageLabel} {instructeur?.pseudo || instructeur?.prenom || ''}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MONTH VIEW */}
      {viewMode === 'month' && (
        <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
          {/* Day headers */}
          <div className="grid grid-cols-7 bg-stone-50 border-b border-stone-200">
            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day, idx) => (
              <div key={idx} className="p-2 md:p-3 text-center text-sm md:text-base font-medium text-stone-600">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar grid */}
          <div className="grid grid-cols-7">
            {monthDays.map((day, index) => {
              const dayCours = getCoursForDay(day);
              const isToday = isSameDay(day, new Date());
              const isCurrentMonth = isSameMonth(day, currentDate);
              const vacancePeriod = isDateInVacances(day);
              
              // Sort courses by start time
              const sortedCours = [...dayCours].sort((a, b) => 
                new Date(a.date_heure_debut) - new Date(b.date_heure_debut)
              );
              
              // Determine background color with priority: today > vacation > other month > normal
              let bgClass = '';
              if (isToday) {
                bgClass = 'bg-green-50/50';
              } else if (vacancePeriod) {
                bgClass = isCurrentMonth ? 'bg-red-50/70' : 'bg-red-50/40';
              } else if (!isCurrentMonth) {
                bgClass = 'bg-stone-50/50';
              }
              
              return (
                <div
                  key={index}
                  onClick={() => handleMonthCellClick(day)}
                  onDoubleClick={(e) => handleMonthCellDoubleClick(e, day)}
                  className={`min-h-[70px] md:min-h-[110px] p-1 md:p-1.5 border-b border-r border-stone-200 last:border-r-0 cursor-pointer hover:bg-stone-50/50 transition-colors ${bgClass}`}
                  title={isInstructor() ? "Double-clic pour créer un cours" : ""}
                >
                  <div className={`text-right mb-1 ${
                    isToday 
                      ? 'font-bold text-[#2E4A3D]' 
                      : vacancePeriod
                        ? 'text-red-600'
                        : isCurrentMonth 
                          ? 'text-stone-700' 
                          : 'text-stone-300'
                  }`}>
                    <span className={`text-sm md:text-base ${isToday ? 'bg-[#2E4A3D] text-white rounded-full px-1.5 md:px-2 py-0.5' : ''}`}>
                      {format(day, 'd')}
                    </span>
                    </div>
                    
                    {/* Course bars - positioned based on time */}
                    <div className="relative h-[45px] md:h-[75px]">
                      {sortedCours.slice(0, 6).map((c, idx) => {
                        const startHour = new Date(c.date_heure_debut).getHours();
                        const startMinutes = new Date(c.date_heure_debut).getMinutes();
                        const duration = c.duree_minutes || 60;
                        
                        // Position based on time (7h-21h = 14h range)
                        const topPercent = Math.max(0, Math.min(100, ((startHour - 7 + startMinutes/60) / 14) * 100));
                        // Bar height based on duration (1h = ~7% of day height)
                        const barHeight = Math.max(5, Math.min(18, (duration / 60) * 8));
                        const themeColor = getThemeColor(c.theme_id);
                        
                        return (
                          <div
                            key={c.id}
                            className="absolute left-0 right-0 rounded-sm cursor-pointer hover:opacity-80 transition-opacity border"
                            style={{
                              backgroundColor: themeColor + '30',
                              borderColor: themeColor,
                              top: `${topPercent}%`,
                              height: `${barHeight}px`,
                              marginBottom: '1px'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectCours(c);
                          }}
                          onDoubleClick={(e) => e.stopPropagation()}
                          title={`${format(new Date(c.date_heure_debut), 'HH:mm')} - ${getCoursTitle(c)}`}
                        />
                      );
                    })}
                    {dayCours.length > 6 && (
                      <div className="absolute bottom-0 right-0 text-[8px] md:text-[10px] text-stone-500 bg-white/80 px-0.5 rounded">
                        +{dayCours.length - 6}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      </div>
      {/* End content wrapper */}

      {/* Course detail modal */}
      {selectedCours && (
        <Dialog open={!!selectedCours} onOpenChange={() => { setSelectedCours(null); setCoursInscrits([]); }}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto p-0">
            {/* Header compact avec couleur du thème */}
            <div 
              className="px-4 pr-12 py-2.5 rounded-t-lg"
              style={{ backgroundColor: getThemeColor(selectedCours.theme_id) + '15' }}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div 
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                    style={{ backgroundColor: getThemeColor(selectedCours.theme_id) }}
                  />
                  <h2 className="font-semibold text-base text-stone-800 truncate">
                    {getCoursTitle(selectedCours)}
                  </h2>
                  {selectedCours.recurrence === 'hebdomadaire' && (
                    <Repeat className="w-3.5 h-3.5 text-stone-500 flex-shrink-0" title="Récurrent" />
                  )}
                </div>
                {/* Places disponibles compact */}
                <div className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-sm font-medium flex-shrink-0 ${
                  coursInscrits.filter(i => !i.absent).length >= selectedCours.places_max 
                    ? 'bg-rose-100 text-rose-700' 
                    : 'bg-white text-stone-700'
                }`}>
                  <span className="font-bold">{selectedCours.places_max - coursInscrits.filter(i => !i.absent).length}</span>
                  <span className="text-xs">place{selectedCours.places_max - coursInscrits.filter(i => !i.absent).length > 1 ? 's' : ''}</span>
                </div>
              </div>
              {/* Ligne d'infos compacte */}
              <div className="flex items-center gap-2 mt-1.5 text-xs text-stone-600 flex-wrap">
                <span className="flex items-center gap-1">
                  <CalendarIcon className="w-3 h-3" />
                  {format(new Date(selectedCours.date_heure_debut), "EEE d MMM", { locale: fr })}
                </span>
                <span className="text-stone-300">•</span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {format(new Date(selectedCours.date_heure_debut), "HH:mm", { locale: fr })} ({selectedCours.duree_minutes}min)
                </span>
                {getInstructeurName(selectedCours.instructeur_id) && (
                  <>
                    <span className="text-stone-300">•</span>
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {getInstructeurName(selectedCours.instructeur_id)}
                    </span>
                  </>
                )}
                {selectedCours.lieu && (
                  <>
                    <span className="text-stone-300">•</span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {selectedCours.lieu}
                    </span>
                  </>
                )}
              </div>
              {/* Badges et groupe sur une ligne */}
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <Badge 
                  className="text-[10px] px-1.5 py-0"
                  style={{ 
                    backgroundColor: getThemeColor(selectedCours.theme_id) + '20',
                    color: getThemeColor(selectedCours.theme_id),
                    border: `1px solid ${getThemeColor(selectedCours.theme_id)}40`
                  }}
                >
                  {courseTypes.find(t => t.value === (selectedCours.type_cours || selectedCours.type_activite))?.label || 'Cours'}
                </Badge>
                {selectedCours.niveau_galop && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-white">
                    Galop {selectedCours.niveau_galop}
                  </Badge>
                )}
                {selectedCours.groupe_fixe_id && (
                  <Badge className="text-[10px] px-1.5 py-0 bg-purple-100 text-purple-700 border border-purple-200">
                    <UsersRound className="w-3 h-3 mr-1" />
                    {getGroupeName(selectedCours.groupe_fixe_id)}
                  </Badge>
                )}
              </div>
            </div>
            
            <div className="p-3 space-y-3">
              {/* Liste des inscrits */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-sm text-stone-800">
                      Cavaliers inscrits
                    </h4>
                    <Badge variant="secondary" className="text-xs">
                      {coursInscrits.filter(i => !i.absent).length} présent(s)
                    </Badge>
                    {coursInscrits.some(i => i.absent) && (
                      <Badge className="bg-rose-100 text-rose-700 text-xs">
                        {coursInscrits.filter(i => i.absent).length} absent(s)
                      </Badge>
                    )}
                  </div>
                  
                  {/* Bouton pour ajouter un cavalier - visible pour les instructeurs */}
                  {isInstructor() && (
                    <Button
                      size="sm"
                      className="bg-[#2E4A3D] hover:bg-[#1E3A2D] text-white"
                      onClick={() => setShowAddCavalierModal(true)}
                      data-testid="add-cavalier-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Ajouter
                    </Button>
                  )}
                </div>
                
                {coursInscrits.length > 0 ? (
                  <div className="space-y-1.5 max-h-[45vh] overflow-y-auto pr-1">
                    {coursInscrits.map((inscrit, idx) => (
                      <div 
                        key={idx} 
                        className={`flex items-center gap-2 p-2 rounded-lg text-sm transition-colors ${
                          inscrit.absent 
                            ? 'bg-rose-50 border border-rose-200' 
                            : inscrit.type === 'ponctuel' 
                              ? 'bg-amber-50/50 border border-amber-100' 
                              : 'bg-stone-50 border border-stone-100'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
                          inscrit.absent 
                            ? 'bg-rose-200 text-rose-700' 
                            : inscrit.type === 'ponctuel'
                              ? 'bg-amber-400 text-white'
                              : 'bg-[#2E4A3D] text-white'
                        }`}>
                          {inscrit.pseudo ? inscrit.pseudo.substring(0, 2).toUpperCase() : `${inscrit.prenom?.[0] || ''}${inscrit.nom?.[0] || ''}`}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`font-medium truncate ${inscrit.absent ? 'text-rose-700 line-through' : 'text-stone-800'}`}>
                              {inscrit.pseudo || `${inscrit.prenom} ${inscrit.nom}`}
                            </p>
                            {inscrit.type === 'ponctuel' && !inscrit.absent && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 flex-shrink-0">
                                Ponctuel
                              </span>
                            )}
                          </div>
                          {inscrit.pseudo && (
                            <p className={`text-xs truncate ${inscrit.absent ? 'text-rose-400' : 'text-stone-500'}`}>
                              {inscrit.prenom} {inscrit.nom}
                            </p>
                          )}
                        </div>
                        
                        {/* Boutons pour les instructeurs */}
                        {isInstructor() && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {/* Toggle absence */}
                            {!inscrit.absent ? (
                              <button
                                onClick={() => handleMarquerAbsent(inscrit.cavalier_id)}
                                className="p-1.5 text-amber-600 hover:bg-amber-100 rounded-md transition-colors"
                                title="Marquer absent"
                              >
                                <UserX className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleAnnulerAbsence(inscrit.cavalier_id)}
                                className="p-1.5 text-green-600 hover:bg-green-100 rounded-md transition-colors"
                                title="Annuler absence"
                              >
                                <User className="w-4 h-4" />
                              </button>
                            )}
                            
                            {/* Bouton retirer - disabled for group members */}
                            {inscrit.type === 'groupe_fixe' ? (
                              <span
                                className="p-1.5 text-stone-300 cursor-not-allowed"
                                title="Membre du groupe - utilisez 'absent' à la place"
                              >
                                <X className="w-4 h-4" />
                              </span>
                            ) : (
                              <button
                                onClick={() => handleRemoveCavalierFromCours(inscrit)}
                                className="p-1.5 text-rose-500 hover:bg-rose-100 rounded-md transition-colors"
                                title="Retirer du cours"
                                data-testid={`remove-cavalier-${inscrit.cavalier_id}`}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-stone-50 rounded-lg border-2 border-dashed border-stone-200">
                    <Users className="w-8 h-8 text-stone-300 mx-auto mb-2" />
                    <p className="text-sm text-stone-500">Aucun cavalier inscrit</p>
                    {isInstructor() && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2"
                        onClick={() => setShowAddCavalierModal(true)}
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        Ajouter un cavalier
                      </Button>
                    )}
                  </div>
                )}
              </div>
              
              {selectedCours.notes && (
                <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
                  <p className="text-xs font-medium text-amber-700 mb-1">Notes</p>
                  <p className="text-sm text-amber-900">{selectedCours.notes}</p>
                </div>
              )}
              
              {/* Actions for non-instructors (cavaliers) */}
              {!isInstructor() && (
                <div className="pt-4 border-t space-y-3">
                  {isCoursePast(selectedCours.date_heure_debut) ? (
                    <div className="p-3 bg-stone-100 rounded-lg text-center">
                      <p className="text-sm text-stone-500">Ce cours est terminé</p>
                    </div>
                  ) : isUserEnrolled ? (
                    // User is enrolled - show absence and desinscription options
                    <div className="space-y-2">
                      {isUserAbsent ? (
                        <Button 
                          className="w-full bg-green-600 hover:bg-green-700 text-white"
                          onClick={handleAnnulerMaAbsence}
                          data-testid="annuler-absence-btn"
                        >
                          Annuler mon absence
                        </Button>
                      ) : (
                        <>
                          <Button 
                            className="w-full bg-rose-600 hover:bg-rose-700 text-white"
                            onClick={handleDeclarerAbsence}
                            data-testid="declarer-absence-btn"
                          >
                            Me déclarer absent(e)
                          </Button>
                          {/* Only show unsubscribe button if NOT a group member */}
                          {!isUserGroupMember && (
                            <Button 
                              variant="outline"
                              className="w-full text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => {
                                handleDesinscription(selectedCours.id);
                                setSelectedCours(null);
                              }}
                              data-testid="desinscription-btn"
                            >
                              Me désinscrire
                            </Button>
                          )}
                          {isUserGroupMember && (
                            <p className="text-xs text-stone-500 text-center mt-2">
                              Inscrit via le groupe - désinscription non autorisée
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  ) : selectedCours.places_disponibles > 0 ? (
                    <Button 
                      className="btn-primary w-full"
                      onClick={async () => {
                        await handleInscription(selectedCours.id);
                        // Refresh enrolled list after inscription
                        try {
                          const inscritsRes = await axios.get(`${API_URL}/api/cours/${selectedCours.id}/inscrits`);
                          setCoursInscrits(inscritsRes.data.inscrits || []);
                        } catch (e) {
                          console.error('Error refreshing inscrits:', e);
                        }
                      }}
                      data-testid="inscription-btn"
                    >
                      S'inscrire à ce cours
                    </Button>
                  ) : (
                    <Button 
                      className="w-full" 
                      disabled
                    >
                      Cours complet
                    </Button>
                  )}
                  
                  {/* Info about 48h rule */}
                  {isUserEnrolled && !isCoursePast(selectedCours.date_heure_debut) && !isUserAbsent && (
                    <p className="text-xs text-stone-400 text-center">
                      Absence à déclarer 48h avant le cours minimum, sinon informer l'instructeur.
                    </p>
                  )}
                </div>
              )}
              
              {/* Actions for instructors */}
              {isInstructor() && (
                <div className="pt-4 border-t space-y-3">
                  {/* Warning for past courses */}
                  {isCoursePast(selectedCours.date_heure_debut) && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800">
                        Ce cours est passé. Vous pouvez toujours le modifier ou l'archiver.
                      </p>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleEditCours(selectedCours)}
                      data-testid="edit-cours-btn"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Modifier
                    </Button>
                    <Button 
                      variant="destructive"
                      onClick={() => handleDeleteCours(selectedCours.id, selectedCours.recurrence === 'hebdomadaire')}
                      data-testid="delete-cours-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Archive button - only for admin instructors and past courses */}
                  {isAdmin() && isCoursePast(selectedCours.date_heure_debut) && (
                    <Button 
                      variant="outline"
                      className="w-full text-amber-700 border-amber-300 hover:bg-amber-50"
                      onClick={() => handleArchiveCours(selectedCours.id)}
                      data-testid="archive-cours-btn"
                    >
                      <Archive className="w-4 h-4 mr-2" />
                      Archiver ce cours
                    </Button>
                  )}
                  
                  {/* Media upload button - for instructors and participants */}
                  <Button 
                    variant="outline"
                    className="w-full text-[#2E4A3D] border-[#2E4A3D]/30 hover:bg-[#2E4A3D]/5"
                    onClick={() => {
                      // Build context with activity + enrolled cavaliers + instructor
                      const cavalierIds = coursInscrits
                        .filter(i => !i.absent)
                        .map(i => i.cavalier_id);
                      setMediaUploadContext({
                        type: 'activity',
                        activityId: selectedCours.id,
                        cavalierIds: cavalierIds,
                        instructorId: selectedCours.instructeur_id
                      });
                      setMediaUploadOpen(true);
                    }}
                    data-testid="add-media-cours-btn"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Ajouter des photos/vidéos
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit course modal */}
      {isEditMode && editingCours && (
        <Dialog open={isEditMode} onOpenChange={() => { setIsEditMode(false); setEditingCours(null); }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">Modifier le cours</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-3 mt-3">
              {/* Row 1: Theme + Type */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium text-stone-600">Thème *</Label>
                  <Select 
                    value={editingCours.theme_id} 
                    onValueChange={(v) => setEditingCours({...editingCours, theme_id: v})}
                  >
                    <SelectTrigger data-testid="edit-theme-select" className="h-9">
                      <SelectValue placeholder="Choisir un thème" />
                    </SelectTrigger>
                    <SelectContent>
                      {themes.map(theme => (
                        <SelectItem key={theme.id} value={theme.id}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: theme.couleur }}
                            />
                            {theme.nom_complet || theme.nom}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium text-stone-600">Type d'activité</Label>
                  <Select 
                    value={editingCours.type_cours || editingCours.type_activite} 
                    onValueChange={(v) => setEditingCours({...editingCours, type_cours: v})}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {courseTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Row 2: Instructeur + Date */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium text-stone-600">Instructeur *</Label>
                  <Select 
                    value={editingCours.instructeur_id} 
                    onValueChange={(v) => setEditingCours({...editingCours, instructeur_id: v})}
                  >
                    <SelectTrigger data-testid="edit-instructeur-select" className="h-9">
                      <SelectValue placeholder="Choisir" />
                    </SelectTrigger>
                    <SelectContent>
                      {allInstructeurs.map(inst => (
                        <SelectItem key={inst.id} value={inst.id}>
                          {inst.pseudo || `${inst.prenom} ${inst.nom}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium text-stone-600">Date et heure *</Label>
                  <Input
                    type="datetime-local"
                    value={editingCours.date_heure_debut}
                    onChange={(e) => setEditingCours({...editingCours, date_heure_debut: e.target.value})}
                    className="input-field h-9"
                    data-testid="edit-datetime"
                  />
                </div>
              </div>
              
              {/* Row 3: Durée + Places + Lieu */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-medium text-stone-600">Durée</Label>
                  {editingCours.type_cours === 'stage' ? (
                    <Select
                      value={String(editingCours.duree_minutes)}
                      onValueChange={(v) => setEditingCours({...editingCours, duree_minutes: parseInt(v)})}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {durationDaysOptions.map(opt => (
                          <SelectItem key={opt.minutes} value={String(opt.minutes)}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Select
                      value={String(editingCours.duree_minutes)}
                      onValueChange={(v) => setEditingCours({...editingCours, duree_minutes: parseInt(v)})}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {durationHoursOptions.map(opt => (
                          <SelectItem key={opt.value} value={String(opt.value)}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-medium text-stone-600">Places max</Label>
                  <Input
                    type="number"
                    value={editingCours.places_max}
                    onChange={(e) => setEditingCours({...editingCours, places_max: parseInt(e.target.value)})}
                    className="input-field h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-stone-600">Lieu</Label>
                  <Input
                    value={editingCours.lieu || ''}
                    onChange={(e) => setEditingCours({...editingCours, lieu: e.target.value})}
                    placeholder="Lieu"
                    className="input-field h-9"
                  />
                </div>
              </div>
              
              {/* Row 4: Groupe + Age + Galop - LOCKED for collective courses */}
              {editingCours.type_cours !== 'particulier' ? (
                // Read-only display for collective courses
                <div className="p-3 bg-stone-50 rounded-lg border border-stone-200">
                  <p className="text-xs text-stone-500 mb-2 flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Champs non modifiables après création
                  </p>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <span className="text-stone-500">Groupe :</span>{' '}
                      <span className="font-medium">
                        {editingCours.groupe_fixe_id 
                          ? groupes.find(g => g.id === editingCours.groupe_fixe_id)?.nom || '-'
                          : 'Aucun'}
                      </span>
                    </div>
                    <div>
                      <span className="text-stone-500">Âge :</span>{' '}
                      <span className="font-medium">
                        {editingCours.categorie_age 
                          ? categoriesAge.find(c => c.value === editingCours.categorie_age)?.label || '-'
                          : 'Tous'}
                      </span>
                    </div>
                    <div>
                      <span className="text-stone-500">Galop :</span>{' '}
                      <span className="font-medium">
                        {editingCours.niveau_galop ? `G${editingCours.niveau_galop}` : 'Tous'}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                // Editable for private courses
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Groupe fixe</Label>
                    <Select 
                      value={editingCours.groupe_fixe_id || 'none'} 
                      onValueChange={(v) => setEditingCours({...editingCours, groupe_fixe_id: v === 'none' ? '' : v})}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Aucun" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun groupe</SelectItem>
                        {groupes.map(groupe => (
                          <SelectItem key={groupe.id} value={groupe.id}>
                            {groupe.nom}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Catégorie d'âge</Label>
                    <Select 
                      value={editingCours.categorie_age || ''} 
                      onValueChange={(v) => setEditingCours({...editingCours, categorie_age: v})}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Tous" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Non spécifié</SelectItem>
                        {categoriesAge.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Niveau Galop</Label>
                    <Select 
                      value={editingCours.niveau_galop || '-'} 
                      onValueChange={(v) => setEditingCours({...editingCours, niveau_galop: v === '-' ? null : v})}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Tous" />
                      </SelectTrigger>
                      <SelectContent>
                        {GALOP_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              
              {/* Récurrence - LOCKED for collective courses */}
              {editingCours.type_cours !== 'particulier' ? (
                <div className="flex items-center gap-2 text-sm text-stone-600">
                  <span className="text-stone-500">Récurrence :</span>
                  <span className="font-medium">
                    {recurrenceTypes.find(t => t.value === editingCours.recurrence)?.label || 'Unique'}
                  </span>
                  <span className="text-xs text-stone-400">(non modifiable)</span>
                </div>
              ) : (
                <div>
                  <Label className="text-xs font-medium text-stone-600">Récurrence</Label>
                  <Select 
                    value={editingCours.recurrence} 
                    onValueChange={(v) => setEditingCours({...editingCours, recurrence: v})}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {recurrenceTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Notes */}
              <div>
                <Label className="text-xs font-medium text-stone-600">Notes</Label>
                <Textarea
                  value={editingCours.notes || ''}
                  onChange={(e) => setEditingCours({...editingCours, notes: e.target.value})}
                  placeholder="Informations complémentaires..."
                  className="input-field min-h-[60px] text-sm"
                />
              </div>
              
              {/* Info message about automatic notification */}
              <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-blue-800">
                  <Bell className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <div>
                    <span className="font-medium">Notification automatique</span>
                    <span className="block text-xs text-blue-600 mt-0.5">
                      Les cavaliers inscrits à ce cours seront automatiquement notifiés de la modification.
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setIsEditMode(false); setEditingCours(null); }}
                >
                  Annuler
                </Button>
                <Button 
                  className="btn-primary flex-1" 
                  onClick={handleUpdateCours}
                  data-testid="update-cours-btn"
                >
                  Enregistrer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Modal pour ajouter un cavalier au cours (instructeurs) */}
      {showAddCavalierModal && selectedCours && (
        <Dialog open={showAddCavalierModal} onOpenChange={setShowAddCavalierModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif">Ajouter un cavalier</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <Label className="form-label">Rechercher un cavalier</Label>
                <Input
                  value={searchCavalier}
                  onChange={(e) => setSearchCavalier(e.target.value)}
                  placeholder="Nom ou pseudo..."
                  className="input-field"
                  data-testid="search-cavalier-input"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
              </div>
              
              <div className="max-h-64 overflow-y-auto space-y-2">
                {filteredCavaliers.length > 0 ? (
                  filteredCavaliers.map(cavalier => {
                    // Check if already enrolled
                    const isEnrolled = coursInscrits.some(i => i.cavalier_id === cavalier.id);
                    
                    return (
                      <div
                        key={cavalier.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          isEnrolled 
                            ? 'bg-stone-100 border-stone-200 opacity-60 cursor-not-allowed' 
                            : 'hover:bg-stone-50 border-stone-200'
                        }`}
                        onClick={() => !isEnrolled && handleAddCavalierToCours(cavalier)}
                        data-testid={`cavalier-option-${cavalier.id}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-[#2E4A3D] text-white flex items-center justify-center text-sm font-medium">
                          {cavalier.pseudo ? cavalier.pseudo.substring(0, 2).toUpperCase() : `${cavalier.prenom?.[0] || ''}${cavalier.nom?.[0] || ''}`}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-stone-800">
                            {cavalier.pseudo || `${cavalier.prenom} ${cavalier.nom}`}
                          </p>
                          <p className="text-xs text-stone-500">{cavalier.prenom} {cavalier.nom}</p>
                        </div>
                        {isEnrolled && (
                          <Badge className="bg-green-100 text-green-700 text-xs">Inscrit</Badge>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-stone-400 text-center py-4">
                    Aucun cavalier trouvé
                  </p>
                )}
              </div>
              
              <Button
                variant="outline"
                className="w-full"
                onClick={() => { setShowAddCavalierModal(false); setSearchCavalier(''); }}
              >
                Fermer
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Modal de confirmation pour retirer un cavalier */}
      <Dialog open={confirmModal.open} onOpenChange={(open) => !open && setConfirmModal({ open: false, type: null, data: null, motif: '', scope: 'one', isRecurring: false })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-center">
              {confirmModal.type === 'remove-cavalier' && 'Confirmer la désinscription'}
              {confirmModal.type === 'delete-cours' && 'Supprimer le cours'}
              {confirmModal.type === 'archive-cours' && 'Archiver le cours'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 text-center">
            <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
              confirmModal.type === 'archive-cours' ? 'bg-amber-100' : 'bg-red-100'
            }`}>
              {confirmModal.type === 'archive-cours' ? (
                <Archive className="w-8 h-8 text-amber-600" />
              ) : confirmModal.type === 'delete-cours' ? (
                <Trash2 className="w-8 h-8 text-red-600" />
              ) : confirmModal.type === 'edit-cours-recurring' ? (
                <Edit2 className="w-8 h-8 text-purple-600" />
              ) : (
                <X className="w-8 h-8 text-red-600" />
              )}
            </div>
            <p className="text-stone-600">
              {confirmModal.type === 'remove-cavalier' && (
                <>Voulez-vous vraiment retirer <span className="font-semibold">{confirmModal.data?.pseudo || confirmModal.data?.prenom || 'ce cavalier'}</span> du cours ? Une notification lui sera envoyée.</>
              )}
              {confirmModal.type === 'delete-cours' && !confirmModal.isRecurring && (
                <>Voulez-vous vraiment <span className="font-semibold text-red-600">supprimer définitivement</span> ce cours ? <br/><span className="text-sm">Tous les cavaliers inscrits seront notifiés.</span></>
              )}
              {confirmModal.type === 'delete-cours' && confirmModal.isRecurring && (
                <>Ce cours fait partie d'une <span className="font-semibold text-purple-600">série récurrente</span>. Que souhaitez-vous supprimer ?</>
              )}
              {confirmModal.type === 'archive-cours' && (
                <>Voulez-vous vraiment <span className="font-semibold">archiver</span> ce cours ? Il ne sera plus visible dans le planning.</>
              )}
              {confirmModal.type === 'edit-cours-recurring' && (
                <>Ce cours fait partie d'une <span className="font-semibold text-purple-600">série récurrente</span>. Que souhaitez-vous modifier ?</>
              )}
            </p>
            
            {/* Options pour cours récurrent - suppression */}
            {confirmModal.type === 'delete-cours' && confirmModal.isRecurring && (
              <div className="mt-4 space-y-2">
                <button
                  onClick={() => setConfirmModal({...confirmModal, scope: 'one'})}
                  className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                    confirmModal.scope === 'one' 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-stone-200 hover:border-stone-300'
                  }`}
                  data-testid="scope-single-btn"
                >
                  <div className="font-medium text-stone-800">Ce cours uniquement</div>
                  <div className="text-sm text-stone-500">Supprimer seulement cette occurrence</div>
                </button>
                <button
                  onClick={() => setConfirmModal({...confirmModal, scope: 'future'})}
                  className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                    confirmModal.scope === 'future' 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-stone-200 hover:border-stone-300'
                  }`}
                  data-testid="scope-future-btn"
                >
                  <div className="font-medium text-stone-800">Cette occurrence et les suivantes</div>
                  <div className="text-sm text-stone-500">Supprimer ce cours et tous les suivants de la série</div>
                </button>
              </div>
            )}
            
            {/* Options pour cours récurrent - modification */}
            {confirmModal.type === 'edit-cours-recurring' && (
              <div className="mt-4 space-y-2">
                <button
                  onClick={() => setConfirmModal({...confirmModal, scope: 'one'})}
                  className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                    confirmModal.scope === 'one' 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-stone-200 hover:border-stone-300'
                  }`}
                  data-testid="edit-scope-single-btn"
                >
                  <div className="font-medium text-stone-800">Ce cours uniquement</div>
                  <div className="text-sm text-stone-500">Modifier seulement cette occurrence</div>
                </button>
                <button
                  onClick={() => setConfirmModal({...confirmModal, scope: 'future'})}
                  className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                    confirmModal.scope === 'future' 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-stone-200 hover:border-stone-300'
                  }`}
                  data-testid="edit-scope-future-btn"
                >
                  <div className="font-medium text-stone-800">Cette occurrence et les suivantes</div>
                  <div className="text-sm text-stone-500">Modifier ce cours et tous les suivants de la série</div>
                </button>
              </div>
            )}
            
            {/* Champ motif pour suppression de cours */}
            {confirmModal.type === 'delete-cours' && (
              <div className="mt-4 text-left">
                <Label className="form-label text-stone-700">Motif de l'annulation (optionnel)</Label>
                <Textarea
                  value={confirmModal.motif || ''}
                  onChange={(e) => setConfirmModal({...confirmModal, motif: e.target.value})}
                  placeholder="Ex: Météo, indisponibilité de l'instructeur..."
                  className="input-field mt-1"
                  rows={2}
                  data-testid="delete-cours-motif"
                />
                <p className="text-xs text-stone-500 mt-1">Ce motif sera inclus dans la notification envoyée aux cavaliers.</p>
              </div>
            )}
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => { setConfirmModal({ open: false, type: null, data: null, motif: '', scope: 'one', isRecurring: false }); setPendingEditCours(null); }}
              data-testid="cancel-action-btn"
            >
              Annuler
            </Button>
            <Button
              className={`flex-1 ${
                confirmModal.type === 'archive-cours' 
                  ? 'bg-amber-600 hover:bg-amber-700' 
                  : confirmModal.type === 'edit-cours-recurring'
                    ? 'bg-purple-600 hover:bg-purple-700'
                    : 'bg-red-600 hover:bg-red-700'
              }`}
              onClick={handleConfirmAction}
              data-testid="confirm-action-btn"
            >
              {confirmModal.type === 'remove-cavalier' && 'Retirer'}
              {confirmModal.type === 'delete-cours' && 'Supprimer'}
              {confirmModal.type === 'archive-cours' && 'Archiver'}
              {confirmModal.type === 'edit-cours-recurring' && 'Continuer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Course Dialog */}
      {isInstructor() && (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif">Créer un cours</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-3 mt-3">
                {/* Row 1: Theme + Type */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Thème *</Label>
                    <Select 
                      value={newCours.theme_id} 
                      onValueChange={(v) => setNewCours({...newCours, theme_id: v})}
                    >
                      <SelectTrigger data-testid="cours-theme-select" className="h-9">
                        <SelectValue placeholder="Choisir un thème" />
                      </SelectTrigger>
                      <SelectContent>
                        {themes.map(theme => (
                          <SelectItem key={theme.id} value={theme.id}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: theme.couleur }}
                              />
                              {theme.nom || theme.nom_complet}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Type d'activité</Label>
                    <Select 
                      value={newCours.type_cours} 
                      onValueChange={(v) => {
                        const updates = { type_cours: v };
                        if (v === 'particulier') {
                          updates.places_max = 1;
                          updates.duree_minutes = 60;
                          updates.recurrence = 'unique';
                        } else if (v === 'stage') {
                          updates.duree_jours = 1;
                          updates.duree_minutes = 480;
                          updates.recurrence = 'unique';
                        } else {
                          updates.places_max = 8;
                          updates.duree_minutes = 60;
                        }
                        setNewCours({...newCours, ...updates});
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {courseTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Row 2: Instructeur + Date */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Instructeur *</Label>
                    <Select 
                      value={newCours.instructeur_id || user?.id} 
                      onValueChange={(v) => setNewCours({...newCours, instructeur_id: v})}
                    >
                      <SelectTrigger data-testid="instructeur-select" className="h-9">
                        <SelectValue placeholder="Choisir" />
                      </SelectTrigger>
                      <SelectContent>
                        {allInstructeurs.map(inst => (
                          <SelectItem key={inst.id} value={inst.id}>
                            {inst.pseudo || `${inst.prenom} ${inst.nom}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Date et heure *</Label>
                    <Input
                      type="datetime-local"
                      value={newCours.date_heure_debut}
                      onChange={(e) => setNewCours({...newCours, date_heure_debut: e.target.value})}
                      className="input-field h-9"
                      data-testid="cours-datetime"
                    />
                  </div>
                </div>
                
                {/* Row 3: Durée + Places + Lieu */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    {newCours.type_cours === 'stage' ? (
                      <>
                        <Label className="text-xs font-medium text-stone-600">Durée</Label>
                        <Select 
                          value={String(newCours.duree_minutes || 480)}
                          onValueChange={(v) => {
                            const opt = durationDaysOptions.find(o => o.minutes === parseInt(v));
                            setNewCours({...newCours, duree_jours: opt?.value || 1, duree_minutes: parseInt(v)});
                          }}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {durationDaysOptions.map(opt => (
                              <SelectItem key={opt.minutes} value={String(opt.minutes)}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </>
                    ) : (
                      <>
                        <Label className="text-xs font-medium text-stone-600">Durée</Label>
                        <Select 
                          value={String(newCours.duree_minutes)}
                          onValueChange={(v) => setNewCours({...newCours, duree_minutes: parseInt(v)})}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {durationHoursOptions.map(opt => (
                              <SelectItem key={opt.value} value={String(opt.value)}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Places max</Label>
                    {newCours.type_cours === 'particulier' ? (
                      <Input type="number" value={1} disabled className="input-field h-9 bg-stone-100" />
                    ) : (
                      <Input
                        type="number"
                        value={newCours.places_max}
                        onChange={(e) => setNewCours({...newCours, places_max: parseInt(e.target.value)})}
                        className="input-field h-9"
                        min={1}
                      />
                    )}
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Lieu</Label>
                    <Input
                      value={newCours.lieu}
                      onChange={(e) => setNewCours({...newCours, lieu: e.target.value})}
                      placeholder="Lieu"
                      className="input-field h-9"
                    />
                  </div>
                </div>
                
                {/* Row 4: Groupe + Age + Galop */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Groupe fixe</Label>
                    <Select 
                      value={newCours.groupe_fixe_id} 
                      onValueChange={(v) => setNewCours({...newCours, groupe_fixe_id: v === 'none' ? '' : v})}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Aucun" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun groupe</SelectItem>
                        {groupes.map(groupe => (
                          <SelectItem key={groupe.id} value={groupe.id}>
                            {groupe.nom}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Catégorie d'âge</Label>
                    <Select 
                      value={newCours.categorie_age} 
                      onValueChange={(v) => setNewCours({...newCours, categorie_age: v})}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Tous" />
                      </SelectTrigger>
                      <SelectContent>
                        {categoriesAge.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Niveau Galop</Label>
                    <Select 
                      value={newCours.niveau_galop || '-'} 
                      onValueChange={(v) => setNewCours({...newCours, niveau_galop: v === '-' ? null : v})}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Tous" />
                      </SelectTrigger>
                      <SelectContent>
                        {GALOP_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Récurrence (only for collectif/balade) */}
                {(newCours.type_cours === 'collectif' || newCours.type_cours === 'balade') && (
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Récurrence</Label>
                    <Select 
                      value={newCours.recurrence} 
                      onValueChange={(v) => {
                        setNewCours({...newCours, recurrence: v});
                        if (v !== 'hebdomadaire') {
                          setBulkCreate(false);
                          setDateFinSaison('');
                        }
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {recurrenceTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {newCours.recurrence === 'hebdomadaire' && (
                      <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="bulk-create-floating"
                            checked={bulkCreate}
                            onChange={(e) => setBulkCreate(e.target.checked)}
                            className="w-4 h-4 rounded border-stone-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <Label htmlFor="bulk-create-floating" className="text-xs font-medium text-emerald-800 cursor-pointer">
                            Créer toutes les occurrences de la saison
                          </Label>
                        </div>
                        {bulkCreate && (
                          <Input
                            type="date"
                            value={dateFinSaison}
                            onChange={(e) => setDateFinSaison(e.target.value)}
                            className="input-field h-9"
                            placeholder="Date de fin"
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Notes */}
                <div>
                  <Label className="text-xs font-medium text-stone-600">Notes (optionnel)</Label>
                  <Textarea
                    value={newCours.notes}
                    onChange={(e) => setNewCours({...newCours, notes: e.target.value})}
                    placeholder="Notes ou instructions particulières..."
                    className="input-field min-h-[60px] text-sm"
                  />
                </div>
                
                {/* Info message about automatic notification for new courses */}
                {newCours.type_cours !== 'particulier' && (
                  <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-blue-800">
                      <Bell className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <div>
                        <span className="font-medium">Notification automatique</span>
                        <span className="block text-xs text-blue-600 mt-0.5">
                          {(() => {
                            const groupeName = newCours.groupe_fixe_id ? groupes.find(g => g.id === newCours.groupe_fixe_id)?.nom : null;
                            const ageCat = newCours.categorie_age ? categoriesAge.find(c => c.value === newCours.categorie_age)?.label : null;
                            const galop = newCours.niveau_galop ? `Galop ${newCours.niveau_galop}` : null;
                            
                            if (groupeName) {
                              return `Les cavaliers du groupe "${groupeName}" seront notifiés.`;
                            } else if (ageCat && galop) {
                              return `Les cavaliers ${ageCat} niveau ${galop} seront notifiés.`;
                            } else if (ageCat) {
                              return `Les cavaliers ${ageCat} seront notifiés.`;
                            } else if (galop) {
                              return `Les cavaliers niveau ${galop} seront notifiés.`;
                            } else {
                              return 'Tous les cavaliers actifs du club seront notifiés.';
                            }
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                <Button 
                  className="btn-primary w-full" 
                  onClick={handleCreateCours}
                  disabled={bulkCreate && !dateFinSaison}
                  data-testid="create-cours-btn"
                >
                  {bulkCreate ? 'Créer tous les cours' : 'Créer le cours'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
      )}
      
      {/* Media Upload Modal */}
      <UploadMediaModal
        open={mediaUploadOpen}
        onClose={() => {
          setMediaUploadOpen(false);
          setMediaUploadContext(null);
        }}
        onSuccess={() => {
          setMediaUploadOpen(false);
          setMediaUploadContext(null);
          toast.success('Médias ajoutés avec succès');
        }}
        context={mediaUploadContext}
      />
    </div>
  );
};
